import { Box, CircularProgress } from "@outschool/backpack";
import {
  ActivateOngoingClassroomV2Mutation,
  ActivateOngoingClassroomV2MutationVariables,
} from "@outschool/gql-frontend-generated";
import { gql, useMutation } from "@outschool/ui-apollo";
import React from "react";

const ActivateOngoingClassroomV2 = gql`
  mutation ActivateOngoingClassroomV2($activityUid: ID!, $sectionUid: ID) {
    activateOngoingClassroomV2(
      activityUid: $activityUid
      sectionUid: $sectionUid
    )
  }
`;

/**
 * Moves an ongoing class to the new classroom on the first visit. The page
 * stays covered until the new classroom has loaded. On failure the page shows
 * the current classroom.
 */
export function OngoingClassroomV2Activation({
  activity,
  sectionUid,
}: {
  activity: { uid: string; needsOngoingClassroomV2Activation: boolean };
  sectionUid?: string | null;
}) {
  const [activate] = useMutation<
    ActivateOngoingClassroomV2Mutation,
    ActivateOngoingClassroomV2MutationVariables
  >(ActivateOngoingClassroomV2, {
    refetchQueries: "active",
    awaitRefetchQueries: true,
  });
  const requestedActivityUidRef = React.useRef<string | null>(null);
  const [settledActivityUid, setSettledActivityUid] = React.useState<
    string | null
  >(null);
  const needsActivation = activity.needsOngoingClassroomV2Activation;

  React.useEffect(() => {
    if (!needsActivation || requestedActivityUidRef.current === activity.uid) {
      return;
    }
    const activityUid = activity.uid;
    requestedActivityUidRef.current = activityUid;
    activate({ variables: { activityUid, sectionUid } })
      .catch(error => OsPlatform.captureError(error))
      .finally(() => {
        if (requestedActivityUidRef.current === activityUid) {
          setSettledActivityUid(activityUid);
        }
      });
  }, [activate, activity.uid, needsActivation, sectionUid]);

  if (!needsActivation || settledActivityUid === activity.uid) {
    return null;
  }
  return (
    <Box
      data-test-id="OngoingClassroomV2Activation"
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: "modal",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "common.white",
      }}
    >
      <CircularProgress />
    </Box>
  );
}
